import type { MouseEvent, ReactNode } from 'react'
import { useState } from 'react'
import { Link, type LinkProps } from 'react-router-dom'

import { useAuth } from '@/features/auth/hooks/useAuth'
import { useProfileQuery } from '@/features/profile/hooks/useProfile'

import { CooldownBlockedModal } from './CooldownBlockedModal'

interface CreateSessionLinkProps extends Omit<LinkProps, 'to'> {
  /** Permite sobrescribir el destino (p.ej. `/sessions/new?from=42`). */
  to?: string
  children: ReactNode
}

function isFutureIso(iso: string | null | undefined): boolean {
  return !!iso && new Date(iso).getTime() > Date.now()
}

/**
 * Drop-in para {@code <Link to="/sessions/new">}. Si el usuario está
 * autenticado y tiene un cooldown_create activo, intercepta el click,
 * abre {@link CooldownBlockedModal} y NO navega. Mismo patrón que la
 * guarda del botón "Unirme" — el usuario ve el mensaje al instante,
 * sin tener que rellenar todo el form para luego fallar.
 *
 * <p>Anónimos y usuarios sin cooldown activo se comportan como un
 * Link normal. Hay un race window mínimo entre montar el componente
 * y que /me/profile responda — durante esos ms el link funciona
 * normal y el backend devolvería el 403 como red de seguridad.</p>
 *
 * <p>Una cuenta TIENDA nunca crea partidas (bloqueado 403 en backend) —
 * el CTA ni siquiera se renderiza, en vez de dejar que el click falle.
 * Gate central: cubre todos los usos (Hero, SiteHeader, MobileTabBar,
 * SiteFooter) sin duplicar el check en cada callsite.</p>
 */
export function CreateSessionLink({
  to = '/sessions/new',
  onClick,
  children,
  ...rest
}: CreateSessionLinkProps) {
  const { isAuthenticated, user } = useAuth()
  // fresh: bypasa el cache para que cambios del cooldownCreateUntil
  // se reflejen al instante en el guard del click.
  const { data: profile } = useProfileQuery({
    enabled: isAuthenticated && user?.role !== 'TIENDA',
    fresh: true,
  })
  const [modalOpen, setModalOpen] = useState(false)

  if (user?.role === 'TIENDA') return null

  const cooldownActive = isFutureIso(profile?.cooldownCreateUntil)

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (cooldownActive) {
      e.preventDefault()
      setModalOpen(true)
      return
    }
    onClick?.(e)
  }

  return (
    <>
      <Link to={to} onClick={handleClick} {...rest}>
        {children}
      </Link>
      {profile?.cooldownCreateUntil && (
        <CooldownBlockedModal
          open={modalOpen}
          kind="create"
          until={profile.cooldownCreateUntil}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  )
}
